import {
  commandLabel,
  type UiCommandRequest,
} from '../../../../agents/context/ui-command-registry.js';
import { useTranslation } from '@govard/i18n-core/react';
import { useState, type ReactElement } from 'react';
import {
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle2,
  Flame,
  Clock,
  Activity,
  CalendarDays,
  ArrowRight,
} from 'lucide-react';
import type { StructuredMessagePayload } from '@govard/ai-chat-kit';
import { Link } from 'react-router-dom';

import { buildChatMyDayCommand, type MyDayScenarioRequest } from '../../lib/myDayScenarios.js';

interface ManagerBriefingSnippetProps {
  sourceMessageId?: string;
  briefing: Extract<StructuredMessagePayload, { type: 'manager_briefing' }>['briefing'];
  onOpenEntity?:
    | ((type: 'deal' | 'contact', id: string, title?: string | undefined) => void)
    | undefined;
  onExecuteScenario?:
    | ((request: string | (MyDayScenarioRequest & { command?: UiCommandRequest })) => void)
    | undefined;
}

export function ManagerBriefingSnippet({
  briefing,
  sourceMessageId,
  onOpenEntity,
  onExecuteScenario,
}: ManagerBriefingSnippetProps): ReactElement {
  const { t, tc, locale } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);

  const hotLeadsCount = briefing.hotLeads.length;
  const overdueTasksCount = briefing.overdueTasks.length;
  const riskDealsCount = briefing.riskDeals.length;
  const todayMeetings = briefing.todayMeetings ?? [];
  const todayMeetingsCount = todayMeetings.length;
  const totalCount = hotLeadsCount + overdueTasksCount + riskDealsCount + todayMeetingsCount;
  const pipeline = briefing.pipeline;
  const pipelineTotal = pipeline
    ? pipeline.new + pipeline.contacted + pipeline.negotiation + pipeline.won + pipeline.lost
    : 0;
  const formatMeetingTime = (value: string): string =>
    new Date(value).toLocaleTimeString(locale === 'ru' ? 'ru-RU' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  const formatPriority = (item: (typeof briefing.priorities)[number]): string =>
    typeof item === 'string' ? item : t(`crm.chat.manager_briefing.priority_${item.kind}`);
  const formatReason = (reason: string): string => {
    const knownReasons = new Set([
      'overdue_next_activity',
      'missing_next_activity',
      'stale_activity',
      'overdue_task',
      'due',
      'high_value',
    ]);
    return knownReasons.has(reason) ? t(`crm.chat.manager_briefing.reason_${reason}`) : reason;
  };

  return (
    <div
      data-testid="structured-snippet-manager-briefing"
      className="my-2 overflow-hidden rounded-xl border border-zinc-200/80 bg-white shadow-2xs transition-all hover:border-zinc-300"
    >
      {/* ─── LobeHub Minimal Monochrome Header ─────────────────────── */}
      <div
        onClick={() => setIsExpanded((prev) => !prev)}
        className="flex cursor-pointer items-center justify-between gap-3 px-3.5 py-2.5 text-xs hover:bg-zinc-50/70 select-none border-b border-zinc-100"
      >
        <div className="flex min-w-0 items-center gap-2">
          <Activity size={14} className="text-zinc-600" />
          <span className="font-semibold text-zinc-900">
            {t('crm.chat.manager_briefing.title')}
          </span>
          <span className="inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-600">
            {tc('crm.chat.manager_briefing.signal_count', totalCount)}
          </span>
        </div>

        <button
          type="button"
          className="flex items-center gap-1 text-[11px] font-medium text-zinc-500 hover:text-zinc-800"
        >
          <span>
            {isExpanded ? t('crm.ai_chat.widget.collapse') : t('crm.ai_chat.widget.expand')}
          </span>
          {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      </div>

      {/* ─── Body ─────────────────────────────────────────────────── */}
      {isExpanded && (
        <div className="text-xs">
          {pipeline && (
            <div className="space-y-2 bg-white p-3.5">
              <div className="flex items-center justify-between text-[11px]">
                <span className="font-medium text-zinc-700">
                  {t('crm.chat.manager_briefing.pipeline_slice')}
                </span>
                <span className="font-mono text-zinc-500">
                  {t('crm.chat.manager_briefing.conversion')}:{' '}
                  <strong className="font-semibold text-zinc-800">
                    {pipeline.conversionRate}%
                  </strong>
                </span>
              </div>
              <div className="flex h-1.5 w-full gap-0.5 overflow-hidden rounded-full bg-zinc-100">
                {[
                  ['bg-blue-500', pipeline.new],
                  ['bg-sky-500', pipeline.contacted],
                  ['bg-orange-500', pipeline.negotiation],
                  ['bg-emerald-500', pipeline.won],
                  ['bg-rose-500', pipeline.lost],
                ].map(([color, value], index) => (
                  <div
                    key={`${color}-${index}`}
                    className={`h-full ${color as string}`}
                    style={{
                      width: `${pipelineTotal === 0 ? 0 : ((value as number) / pipelineTotal) * 100}%`,
                    }}
                  />
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-3 pt-0.5 font-mono text-[11px] text-zinc-500">
                {[
                  ['bg-blue-500', 'new', pipeline.new],
                  ['bg-sky-500', 'contacted', pipeline.contacted],
                  ['bg-orange-500', 'negotiation', pipeline.negotiation],
                  ['bg-emerald-500', 'won', pipeline.won],
                  ['bg-rose-500', 'lost', pipeline.lost],
                ].map(([color, label, value]) => (
                  <span key={label as string} className="flex items-center gap-1.5">
                    <span className={`h-1.5 w-1.5 rounded-full ${color as string}`} />
                    <span>{t(`crm.chat.manager_briefing.${label as string}`)}</span>
                    <span className="font-semibold text-zinc-700">{value as number}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Daily signals: each item links to the source record where possible. */}
          <div className="grid grid-cols-1 divide-y divide-zinc-100 border-y border-zinc-100 md:grid-cols-2 md:divide-x xl:grid-cols-4 xl:divide-y-0">
            {/* Today's meetings */}
            <div className="space-y-2 p-3">
              <div className="flex items-center justify-between text-[11px]">
                <span className="flex items-center gap-1.5 font-medium text-zinc-800">
                  <CalendarDays size={12} className="text-blue-600" />
                  {t('crm.chat.manager_briefing.today_meetings')}
                </span>
                <span className="font-mono text-[10px] text-zinc-400">{todayMeetingsCount}</span>
              </div>
              <ul className="space-y-1">
                {todayMeetings.map((meeting) => (
                  <li key={meeting.id}>
                    <Link
                      to={`/meetings/${meeting.id}`}
                      className="flex items-center justify-between rounded-md p-1.5 transition hover:bg-zinc-50 group"
                    >
                      <span className="truncate font-medium text-zinc-800 group-hover:text-zinc-950">
                        {meeting.title}
                      </span>
                      <span className="ml-1 shrink-0 font-mono text-[10.5px] text-zinc-400">
                        {formatMeetingTime(meeting.startsAt)}
                      </span>
                    </Link>
                    {(meeting.contactName ?? meeting.dealTitle) && (
                      <span className="block truncate px-1.5 text-[10.5px] text-zinc-400">
                        {meeting.contactName ?? meeting.dealTitle}
                      </span>
                    )}
                  </li>
                ))}
                {todayMeetingsCount === 0 && (
                  <li className="py-1 text-center text-[10.5px] italic text-zinc-400">—</li>
                )}
              </ul>
            </div>
            {/* Column 1: Hot Leads */}
            <div className="p-3 space-y-2">
              <div className="flex items-center justify-between text-[11px]">
                <span className="flex items-center gap-1.5 font-medium text-zinc-800">
                  <Flame size={12} className="text-rose-600" />
                  {t('crm.chat.manager_briefing.hot_leads')}
                </span>
                <span className="font-mono text-[10px] text-zinc-400">{hotLeadsCount}</span>
              </div>
              <ul className="space-y-1">
                {briefing.hotLeads.map((lead) => (
                  <li
                    key={lead.id}
                    onClick={() => onOpenEntity?.('deal', lead.id, lead.title)}
                    className="flex cursor-pointer items-center justify-between rounded-md p-1.5 hover:bg-zinc-50 transition group"
                  >
                    <span
                      className="min-w-0 flex-1 truncate text-zinc-800 group-hover:text-zinc-950 font-medium"
                      title={lead.title}
                    >
                      {lead.title}
                    </span>
                    <span
                      className="shrink-0 truncate text-[10.5px] text-zinc-400 font-normal max-w-[45%]"
                      title={formatReason(lead.reason)}
                    >
                      {formatReason(lead.reason)}
                    </span>
                  </li>
                ))}
                {hotLeadsCount === 0 && (
                  <li className="py-1 text-center text-[10.5px] text-zinc-400 italic">—</li>
                )}
              </ul>
            </div>

            {/* Column 2: Overdue Tasks */}
            <div className="p-3 space-y-2">
              <div className="flex items-center justify-between text-[11px]">
                <span className="flex items-center gap-1.5 font-medium text-zinc-800">
                  <Clock size={12} className="text-amber-600" />
                  {t('crm.chat.manager_briefing.overdue_tasks')}
                </span>
                <span className="font-mono text-[10px] text-zinc-400">{overdueTasksCount}</span>
              </div>
              <ul className="space-y-1">
                {briefing.overdueTasks.map((task) => (
                  <li
                    key={task.id}
                    className="flex items-center justify-between rounded-md p-1.5 hover:bg-zinc-50 transition group"
                  >
                    <Link
                      to={`/tasks/${task.id}`}
                      className="min-w-0 flex-1 truncate font-medium text-zinc-800 hover:text-zinc-950"
                      title={task.title}
                    >
                      {task.title}
                    </Link>
                    {task.dealTitle && (
                      <span
                        className="shrink-0 truncate text-[10.5px] text-zinc-400 font-normal max-w-[45%]"
                        title={task.dealTitle}
                      >
                        {task.dealTitle}
                      </span>
                    )}
                  </li>
                ))}
                {overdueTasksCount === 0 && (
                  <li className="py-1 text-center text-[10.5px] text-zinc-400 italic">—</li>
                )}
              </ul>
            </div>

            {/* Column 3: Risk Deals */}
            <div className="p-3 space-y-2">
              <div className="flex items-center justify-between text-[11px]">
                <span className="flex items-center gap-1.5 font-medium text-zinc-800">
                  <AlertTriangle size={12} className="text-orange-600" />
                  {t('crm.chat.manager_briefing.risk_deals')}
                </span>
                <span className="font-mono text-[10px] text-zinc-400">{riskDealsCount}</span>
              </div>
              <ul className="space-y-1">
                {briefing.riskDeals.map((risk) => (
                  <li
                    key={risk.id}
                    onClick={() => onOpenEntity?.('deal', risk.id, risk.title)}
                    className="flex cursor-pointer items-center justify-between rounded-md p-1.5 hover:bg-zinc-50 transition group"
                  >
                    <span
                      className="min-w-0 flex-1 truncate text-zinc-800 group-hover:text-zinc-950 font-medium"
                      title={risk.title}
                    >
                      {risk.title}
                    </span>
                    <span
                      className="shrink-0 truncate text-[10.5px] text-zinc-400 font-normal max-w-[45%]"
                      title={formatReason(risk.reason)}
                    >
                      {formatReason(risk.reason)}
                    </span>
                  </li>
                ))}
                {riskDealsCount === 0 && (
                  <li className="py-1 text-center text-[10.5px] text-zinc-400 italic">—</li>
                )}
              </ul>
            </div>
          </div>

          {/* Key Priorities: Clean Numbered List */}
          {briefing.priorities.length > 0 && (
            <div className="p-3.5 bg-zinc-50/50 space-y-2">
              <span className="font-medium text-zinc-700 text-[11px]">
                {t('crm.chat.manager_briefing.priorities')}
              </span>
              <div className="flex flex-wrap gap-2">
                {briefing.priorities.map((item, idx) => {
                  const kind = typeof item === 'string' ? null : item.kind;
                  const count = typeof item === 'string' ? null : item.count;
                  const hasCount = typeof count === 'number';
                  const label = formatPriority(item);
                  const action =
                    kind === null
                      ? null
                      : buildChatMyDayCommand(
                          { ...briefing, meetings: briefing.todayMeetings ?? [] },
                          kind,
                          sourceMessageId ?? '',
                        );
                  const canAct =
                    action !== null &&
                    sourceMessageId !== undefined &&
                    onExecuteScenario !== undefined;
                  return (
                    <button
                      key={idx}
                      type="button"
                      data-testid={
                        kind === null
                          ? 'manager-briefing-priority-chip'
                          : `manager-briefing-priority-action-${kind}`
                      }
                      aria-label={hasCount ? `${label}: ${count}` : label}
                      disabled={!canAct}
                      title={canAct ? label : undefined}
                      onClick={
                        canAct
                          ? () => {
                              onExecuteScenario?.(
                                action === null
                                  ? label
                                  : {
                                      prompt: commandLabel(action, locale === 'en' ? 'en' : 'ru'),
                                      command: action,
                                    },
                              );
                            }
                          : undefined
                      }
                      className={`inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11.5px] font-medium shadow-2xs transition ${
                        canAct
                          ? 'cursor-pointer border-zinc-200/80 bg-white text-zinc-700 hover:border-blue-300 hover:bg-blue-50/60 hover:text-blue-700'
                          : 'border-zinc-200/80 bg-white text-zinc-700'
                      }`}
                    >
                      <CheckCircle2 size={12} className="shrink-0 text-zinc-500" />
                      <span className="min-w-0">{label}</span>
                      {hasCount ? (
                        <span className="shrink-0 rounded bg-zinc-100 px-1 font-mono text-[10.5px] leading-4 text-zinc-600">
                          {count}
                        </span>
                      ) : null}
                      {canAct ? <ArrowRight size={12} className="shrink-0 text-zinc-400" /> : null}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
